import { useParams } from 'react-router';
import { ErrorBlock, LoadingBlock } from '../components/states';
import { useUnsubscribeActions, useUnsubscribeState } from '../lib/queries';

/**
 * MSG-03 from her side. The screen a link at the bottom of an announcement leads to, and the only
 * screen in the app that renders without a session — see the routing in `App.tsx`, which matches it
 * ahead of the sign-in gate for the same reason it matches the emailed sign-in link there.
 *
 * It shows what will happen before it happens. Following a link and finding the decision already
 * made is unsettling even when the decision was the one wanted, and it is wrong outright when a
 * mail client prefetched the URL.
 */
export function UnsubscribePage() {
  const { messageId = '', signature = '' } = useParams();
  const state = useUnsubscribeState(messageId, signature);
  const { unsubscribe, resubscribe } = useUnsubscribeActions(messageId, signature);

  if (state.isPending) {
    return <LoadingBlock label="Checking that link…" />;
  }

  if (state.isError || !state.data) {
    return (
      <Card>
        {/* No retry: a signature that does not verify will not verify on the third attempt, and
            the API answers the same way whether the link is wrong or the message is long gone. */}
        <ErrorBlock message="That unsubscribe link is not valid." />
        <p className="text-subtitle mt-2 text-ink-3">
          Links in older emails still work. If none of them do, ask the organizers to take you off
          the list.
        </p>
      </Card>
    );
  }

  const { email, eventName, unsubscribed } = state.data;
  const busy = unsubscribe.isPending || resubscribe.isPending;

  return (
    <Card>
      <p className="text-eyebrow tracking-eyebrow font-bold text-ink-3 uppercase">{eventName}</p>

      {unsubscribed ? (
        <>
          <h1 className="font-display text-screen-title mt-2 font-semibold text-ink">
            You are unsubscribed
          </h1>
          <p className="text-body mt-2 text-ink-3">
            No more announcements or morning schedules will go to {email}. Your sign-in link still
            works, and so do notices about a session you saved that has moved.
          </p>

          <button
            type="button"
            onClick={() => resubscribe.mutate()}
            disabled={busy}
            className="text-button rounded-button mt-5 flex min-h-touch w-full items-center justify-center border border-border-input bg-surface px-4 font-semibold text-ink disabled:opacity-60"
          >
            {resubscribe.isPending ? 'Turning them back on…' : 'Actually, send them again'}
          </button>

          {/* A mis-tap has to be undoable or the button is a trap, and the undo is worth stating
              plainly rather than hiding behind a smaller word. */}
          <p className="text-subtitle mt-3 text-ink-3">
            Tapped that by mistake? This puts you back on the list straight away.
          </p>
        </>
      ) : (
        <>
          <h1 className="font-display text-screen-title mt-2 font-semibold text-ink">
            Stop these emails?
          </h1>
          <p className="text-body mt-2 text-ink-3">
            We will stop sending announcements and the optional morning schedule to {email}.
          </p>

          <button
            type="button"
            onClick={() => unsubscribe.mutate()}
            disabled={busy}
            className="text-button rounded-button mt-5 flex min-h-touch w-full items-center justify-center bg-ink px-4 font-semibold text-white disabled:opacity-60"
          >
            {unsubscribe.isPending ? 'Unsubscribing…' : 'Unsubscribe'}
          </button>

          {/* The fear that stops people unsubscribing from an event they are actually attending is
              that it will lock them out of it. Saying otherwise is the point of this screen. */}
          <p className="text-subtitle mt-3 text-ink-3">
            Your sign-in link will keep working, and you will still be told if a session you saved
            moves or is cancelled.
          </p>
        </>
      )}

      {(unsubscribe.isError || resubscribe.isError) && (
        <p className="text-subtitle mt-3 font-semibold text-alert-text" role="status">
          That did not go through. Check your connection and try again.
        </p>
      )}
    </Card>
  );
}

/**
 * Its own centred card rather than the app's screen layout: there is no session here, so a tab bar
 * would offer four screens that would all answer with the sign-in form.
 */
function Card({ children }: { children: React.ReactNode }) {
  return (
    <main className="px-gutter flex min-h-dvh items-center justify-center py-8">
      <div className="rounded-card w-full max-w-md border border-border bg-surface p-5">
        {children}
      </div>
    </main>
  );
}
